import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import type { components } from "@/types/api.generated";
export type { Team } from "@/types/entities";
import { toast } from "sonner";

export interface TeamFilters {
  cursor?: string;
  limit?: number;
}

export function useTeams(filters: TeamFilters = {}) {
  return useQuery({
    queryKey: ["teams", filters],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/teams", {
        params: { query: filters },
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
  });
}

export function useCreateTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["CreateTeamRequest"]) => {
      const { data, error } = await apiClient.POST("/v1/teams", { body });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["teams"] });
      toast.success("Team created");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateTeam(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["UpdateTeamRequest"]) => {
      const { data, error } = await apiClient.PUT("/v1/teams/{id}", {
        params: { path: { id } },
        body,
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["teams"] });
      toast.success("Team updated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await apiClient.DELETE("/v1/teams/{id}", {
        params: { path: { id } },
      });
      if (error) throw new Error(error.error.message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["teams"] });
      toast.success("Team deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
